import type {
  FileReviewAnchor,
  FileReviewAnchorKind,
  FileReviewMessage,
  FileReviewThreadDto
} from './types';

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

function pick(raw: Record<string, unknown>, camel: string, pascal: string): unknown {
  return raw[camel] ?? raw[pascal];
}

function asString(value: unknown): string {
  return value == null ? '' : String(value);
}

function asNullableString(value: unknown): string | null {
  if (value == null || value === '') return null;
  return String(value);
}

function normalizeMessage(raw: unknown): FileReviewMessage {
  const m = asRecord(raw);
  return {
    id: asString(pick(m, 'id', 'Id')),
    anchorId: asNullableString(pick(m, 'anchorId', 'AnchorId')),
    anchorKind: asNullableString(pick(m, 'anchorKind', 'AnchorKind')) as FileReviewAnchorKind | null,
    text: asString(pick(m, 'text', 'Text')),
    submittedBy: asString(pick(m, 'submittedBy', 'SubmittedBy')),
    submittedByUserId: asNullableString(pick(m, 'submittedByUserId', 'SubmittedByUserId')),
    submittedByDisplayName: asNullableString(
      pick(m, 'submittedByDisplayName', 'SubmittedByDisplayName')
    ),
    authorDisplayName: asNullableString(pick(m, 'authorDisplayName', 'AuthorDisplayName')),
    authorHasAvatar: Boolean(pick(m, 'authorHasAvatar', 'AuthorHasAvatar')),
    sourceSite: asString(pick(m, 'sourceSite', 'SourceSite')),
    submittedAt: asString(pick(m, 'submittedAt', 'SubmittedAt')),
    updatedAt: asNullableString(pick(m, 'updatedAt', 'UpdatedAt')),
    anchorSelectedText: asNullableString(pick(m, 'anchorSelectedText', 'AnchorSelectedText'))
  };
}

function normalizeAnchor(raw: unknown): FileReviewAnchor {
  const a = asRecord(raw);
  const sheetIndexRaw = pick(a, 'sheetIndex', 'SheetIndex');
  return {
    id: asString(pick(a, 'id', 'Id')),
    kind: asString(pick(a, 'kind', 'Kind')) as FileReviewAnchorKind,
    selectedText: asString(pick(a, 'selectedText', 'SelectedText')),
    createdBy: asString(pick(a, 'createdBy', 'CreatedBy')),
    createdByDisplayName: asNullableString(pick(a, 'createdByDisplayName', 'CreatedByDisplayName')),
    sourceSite: asString(pick(a, 'sourceSite', 'SourceSite')),
    createdAt: asString(pick(a, 'createdAt', 'CreatedAt')),
    pageIndex: pick(a, 'pageIndex', 'PageIndex') != null ? Number(pick(a, 'pageIndex', 'PageIndex')) : undefined,
    areas: (pick(a, 'areas', 'Areas') as FileReviewAnchor['areas']) ?? undefined,
    sheetIndex: sheetIndexRaw != null && sheetIndexRaw !== '' ? Number(sheetIndexRaw) : undefined,
    sheetName: asNullableString(pick(a, 'sheetName', 'SheetName')) ?? undefined,
    ranges: (pick(a, 'ranges', 'Ranges') as FileReviewAnchor['ranges']) ?? undefined,
    wordRange: (pick(a, 'wordRange', 'WordRange') as FileReviewAnchor['wordRange']) ?? undefined
  };
}

/** Normalize thread JSON whether the host serializes camelCase or PascalCase. */
export function normalizeFileReviewThread(raw: unknown): FileReviewThreadDto {
  const t = asRecord(raw);
  const rawMessages = pick(t, 'messages', 'Messages');
  const rawAnchors = pick(t, 'anchors', 'Anchors');
  return {
    threadId: asString(pick(t, 'threadId', 'ThreadId')),
    mountId: asString(pick(t, 'mountId', 'MountId')),
    path: asString(pick(t, 'path', 'Path')),
    anchors: Array.isArray(rawAnchors) ? rawAnchors.map(normalizeAnchor) : [],
    messages: Array.isArray(rawMessages) ? rawMessages.map(normalizeMessage) : [],
    canAnnotate: Boolean(pick(t, 'canAnnotate', 'CanAnnotate'))
  };
}
